const SHOEBOX_DATA_NAME = 'siteconfig';
const SITECONFIG_URL = undefined;

export interface Logger {
  log(...args: unknown[]): void;
  info(...args: unknown[]): void;
  warn(...args: unknown[]): void;
  error(...args: unknown[]): void;
  time(label?: string): void;
  timeEnd(label?: string): void;
}

export interface FastbootRequest {
  /** The cookies for the current request. */
  cookies: Record<string, unknown>;

  /** The headers for the current request. */
  headers: Pick<Headers, 'has' | 'get'>;

  /**
   * The host of the request that the current FastBoot server is responding to
   * (`example.com` or `localhost:3000`).
   * Retrieving host will error on 2 conditions:
   * 1. you do not have a hostWhitelist defined
   * 2. the Host header does not match an entry in your hostWhitelist
   */
  host: string;

  /** The query parameters for the current request, as a key-value pair. */
  queryParams: Record<string, unknown>;

  /**
   * The path (`/` or `/some-path`) of the request that the current FastBoot
   * server is responding to.
   */
  path: string;

  /**
   * The protocol (`http:` or `https:`) of the request that the current
   * FastBoot server is responding to.
   */
  protocol: string;

  /**
   * The request type of the request that the current
   * FastBoot server is responding to.
   */
  method: string;

  /** Contains key-value pairs of data submitted in the request body. */
  body: Record<string, unknown>;
}

export interface Shoebox {
  put(key: string, value: unknown): void;
  retrieve(key: string): unknown;
}

export interface FastBoot {
  /** Allows you to check if you are running within FastBoot. */
  isFastBoot: boolean;

  /**
   * A request object which exposes details about the current request being
   * handled by FastBoot.
   */
  request: FastbootRequest;

  /**
   * You can pass application state from the FastBoot rendered application to
   * the browser rendered application using a feature called the "Shoebox".
   * This allows you to leverage server API calls made by the FastBoot rendered
   * application on the browser rendered application. Thus preventing you from
   * duplicating work that the FastBoot application is performing. This should
   * result in a performance benefit for your browser application, as it does
   * not need to issue server API calls whose results are available from the
   * Shoebox.
   */
  shoebox: Shoebox;

  /**
   * By default, FastBoot waits for the `beforeModel`, `model`, and
   * `afterModel` hooks to resolve before sending the response back to the
   * client. If you have asynchrony that runs outside of those contexts, your
   * response may not reflect the state that you want.
   * To solve this, the `fastboot` service has `deferRendering` method that
   * accepts a promise. It will chain all promises passed to it, and the
   * FastBoot server will wait until all of these promises resolve before
   * sending the response to the client. These promises must be chained before
   * the rendering is complete after the model hooks. For example, if a
   * component that is rendered into the page makes an async call for data,
   * registering a promise to be resolved in its `init` hook would allow the
   * component to defer the rendering of the page.
   */
  deferRendering(promise: Promise<unknown>): void;
}

const _getURLLocale = function (defaultLocale: string, fastboot?: FastBoot): string {
  let path;
  if (fastboot && fastboot.isFastBoot) {
    // You're on the server - no default host
    path = fastboot.request.path;
  } else {
    path = window.location.pathname;
  }

  const pos = path.indexOf('/designtools/');
  if (pos !== -1) {
    const start = pos + '/designtools/'.length;
    return path.substring(start, start + 2);
  } else {
    return defaultLocale;
  }
};

interface LocaleSet {
  full: string;
  adi: string;
  iso: string;
  cdp: string;
}

interface SiteLocales {
  AdiLocale: string;
  IsoLocale: string;
  CdpLocale: string;
  FullLocale: string;
}

const locales: Record<string, LocaleSet> = {
  en: { full: 'en-us', adi: 'en', iso: 'en', cdp: 'en' },
  zh: { full: 'zh-cn', adi: 'cn', iso: 'zh', cdp: 'zh' },
  cn: { full: 'zh-cn', adi: 'cn', iso: 'zh', cdp: 'zh' },
  ja: { full: 'ja-jp', adi: 'jp', iso: 'ja', cdp: 'jp' },
  jp: { full: 'ja-jp', adi: 'jp', iso: 'ja', cdp: 'jp' },
  ru: { full: 'ru-ru', adi: 'ru', iso: 'ru', cdp: 'ru' },
};

const _getLocales = function (defaultLocale: string, supportedLocales: string[], fastboot?: FastBoot): SiteLocales {
  const urlLocale = _getURLLocale(defaultLocale, fastboot);
  const localeSet: LocaleSet | undefined = locales[urlLocale];
  const useDefault =
    !localeSet || (!supportedLocales.includes(localeSet.full) && !supportedLocales.includes(localeSet.iso));
  const foundLocale = locales[useDefault ? 'en' : urlLocale] as LocaleSet; // cannot be undefined
  return {
    AdiLocale: foundLocale.adi,
    IsoLocale: foundLocale.iso,
    CdpLocale: foundLocale.cdp,
    FullLocale: foundLocale.full,
  };
};

const _getURL = function (url: string, fastboot: FastBoot | undefined, logger: Logger) {
  if (fastboot && fastboot.isFastBoot) {
    // You're on the server - no default host
    if (url[0] === '/') {
      let protocol = fastboot.request.protocol;
      if (!protocol) {
        protocol = 'http:';
      }
      let host = '---';
      try {
        host = fastboot.request.host;
      } catch (e) {
        logger.error(`Error getting FastBoot host: ${e}`);
        throw e;
      }
      return `${protocol}//${host}${url}`;
    } else {
      return url;
    }
  } else {
    // in browser
    return url;
  }
};

const _fixLocalUrl = function (url: string, rootUrl: string) {
  const idx = url.indexOf('{root}');
  if (idx >= 0) {
    const prefix = url.substring(0, idx);
    const suffix = url.substring(idx + '{root}'.length);
    if (rootUrl.endsWith('/')) {
      return prefix + rootUrl + (suffix.startsWith('/') ? suffix.slice(1) : suffix);
    } else {
      return prefix + rootUrl + (suffix.startsWith('/') ? suffix : '/' + suffix);
    }
  }
  return url;
};

const _replaceLocale = function (text: string, locales: SiteLocales) {
  if (locales.AdiLocale) {
    return text
      .replace('{0}', locales.AdiLocale)
      .replace('{adi}', locales.AdiLocale)
      .replace('{iso}', locales.IsoLocale)
      .replace('{cdp}', locales.CdpLocale)
      .replace('{full}', locales.FullLocale);
  } else {
    return text;
  }
};

const _loadConfigFromJson = function (
  url: string,
  locales: SiteLocales,
  rootUrl: string,
  logger: Logger,
  fastboot?: FastBoot,
) {
  let request;

  const _logger = logger
    ? logger
    : {
        info(...args: unknown[]) {
          console.info(...args);
        }, // eslint-disable-line no-console
        error(...args: unknown[]) {
          console.error(...args);
        }, // eslint-disable-line no-console
      };

  const _url =
    typeof SITECONFIG_URL !== 'undefined' && SITECONFIG_URL ? SITECONFIG_URL : _getURL(url, fastboot, logger); //eslint-disable-line no-undef
  const shoebox = fastboot ? fastboot.shoebox : null;

  if (fastboot && fastboot.isFastBoot) {
    _logger.info(`Loading site configuration from ${_url} in fastboot`);
    request = fetch(_url)
      .then((response) => {
        return response.json();
      })
      .then((config) => {
        Object.keys(config).forEach((item) => {
          config[item] = _fixLocalUrl(_replaceLocale(config[item], locales), rootUrl);
        });
        shoebox && shoebox.put(SHOEBOX_DATA_NAME, config);
        return config;
      })
      .catch((error) => {
        _logger.error(`Load of site configuration at ${_url} failed: ${error}`);
        throw error;
      });
  } else {
    const cache = shoebox && shoebox.retrieve(SHOEBOX_DATA_NAME);
    if (cache) {
      _logger.info(`Loading site configuration from shoebox in browser`);
      request = Promise.resolve(cache);
    } else {
      _logger.info(`Loading site configuration from ${_url} in browser`);
      request = fetch(_url)
        .then((response) => {
          return response.json();
        })
        .then((config) => {
          Object.keys(config).forEach((item) => {
            config[item] = _fixLocalUrl(_replaceLocale(config[item], locales), rootUrl);
          });
          return config;
        })
        .catch((error) => {
          _logger.error(`Load of site configuration at ${_url} failed: ${error}`);
          throw error;
        });
    }
  }

  return request;
};

export function start(
  target: unknown,
  url: string,
  rootUrl: string,
  defaultLocale: string,
  supportedlocales: string[],
  logger: Logger,
  fastboot?: FastBoot,
): Promise<Record<string, string> & SiteLocales> {
  const locales = _getLocales(defaultLocale, supportedlocales, fastboot);
  return _loadConfigFromJson(url, locales, rootUrl, logger, fastboot).then((data) => {
    data.AdiLocale = locales.AdiLocale;
    data.FullLocale = locales.FullLocale;
    data.IsoLocale = locales.IsoLocale;
    data.CdpLocale = locales.CdpLocale;
    Object.keys(data).forEach((item) => {
      Object.defineProperty(target, item, {
        get: function () {
          return data[item];
        },
      });
    });
    return data;
  });
}
